// @utils/locales.js
// Edge-safe: used by middleware, server components and (pure helpers only) client components.
import { fetchCms } from '@utils/fetchCms'

export const LOCALE_RE = /^[a-z]{2}(?:-[a-z]{2})?$/

// Language facts, not site data
const RTL_LANGS = new Set(['ar', 'he', 'fa', 'ur', 'ps', 'yi', 'dv', 'ku'])
export const isRtl = (locale) => RTL_LANGS.has(String(locale ?? '').split('-')[0])
export const looksLikeLocale = (segment) => LOCALE_RE.test(String(segment ?? ''))

export function normalizeLocales(data) {
  const locales = [...new Set(
    (data?.locales || [])
      .map(l => String(l ?? '').trim().toLowerCase())
      .filter(l => LOCALE_RE.test(l))
  )]

  let defaultLocale = String(data?.defaultLocale ?? '').trim().toLowerCase()
  if (!LOCALE_RE.test(defaultLocale)) defaultLocale = locales[0] || 'en'
  if (!locales.includes(defaultLocale)) locales.unshift(defaultLocale)

  return { locales, defaultLocale }
}

// Used only until the server has answered once (cold start + API down).
// isFallback tells callers the real list is unknown, so they accept any
// locale-shaped segment instead of redirecting real /th/... URLs away.
const FALLBACK_DEFAULT = process.env.NEXT_PUBLIC_DEFAULT_LOCALE || 'en'
export const FALLBACK_LOCALES = {
  ...normalizeLocales({ locales: [FALLBACK_DEFAULT], defaultLocale: FALLBACK_DEFAULT }),
  isFallback: true,
}

// Is this URL segment a language this site serves?
export function isSiteLocale(segment, site) {
  if (site.locales.includes(segment)) return true
  return !!site.isFallback && looksLikeLocale(segment)
}

// Default locale has no URL prefix; others are /{locale}/...
export function localePath(locale, path = '/', defaultLocale = FALLBACK_LOCALES.defaultLocale) {
  const clean = '/' + String(path).replace(/^\/+/, '')
  if (locale === defaultLocale) return clean
  return clean === '/' ? `/${locale}` : `/${locale}${clean}`
}

// ── Runtime list with an in-memory cache (per server instance) ──────────────
const TTL = 1 * 60 * 1000          // refresh every 5 min
const RETRY_AFTER = 30 * 1000      // after a failure, retry in 30s

let cached = null
let expiresAt = 0
let inflight = null

function refresh() {
  if (!inflight) {
    inflight = fetchCms('/content/locales', { timeout: 1500, revalidate: 300 })
      .then((data) => {
        console.log('raw locales response:', JSON.stringify(data))   // ← add this line
        cached = normalizeLocales(data)
        expiresAt = Date.now() + TTL
        return cached
      })
      .catch((err) => {
        console.error('getSiteLocales failed:', err.message)
        expiresAt = Date.now() + RETRY_AFTER   // keep serving the last good value
        return cached || FALLBACK_LOCALES
      })
      .finally(() => { inflight = null })
  }
  return inflight
}

export async function getSiteLocales() {
  if (cached && Date.now() < expiresAt) return cached

  // Stale-while-revalidate: after the first load, never make a request wait
  if (cached) {
    refresh()
    return cached
  }
  return refresh()
}